import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../../lib/api';

interface User {
  id: string;
  email: string;
  first_name: string;
  middle_name: string | null;
  last_name: string;
  full_name: string;
  phone: string | null;
  avatar_url: string | null;
  gender: string | null;
  date_of_birth: string | null;
  id_type: string | null;
  id_number: string | null;
  country: string | null;
  city: string | null;
  zipcode: string | null;
  website_url: string | null;
  linkedin_url: string | null;
  language: string;
  timezone: string | null;
  is_super_admin: boolean;
  schools: Array<{
    id: string;
    name: string;
    role: string;
    logo_url: string | null;
    primary_color: string;
    secondary_color: string;
  }>;
}

interface LoginResponse {
  token: string;
  user: User;
}

interface MeResponse {
  user: User;
}

export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isSuperAdmin: boolean;
  userRole: 'super_admin' | 'admin' | 'teacher' | 'student' | null;
  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  currentSchool: { id: string; name: string; role: string; logo_url: string | null; primary_color: string; secondary_color: string } | null;
  setCurrentSchool: (schoolId: string) => void;
}

interface RegisterData {
  email: string;
  password: string;
  password_confirmation: string;
  first_name: string;
  last_name: string;
  phone?: string;
  school_name?: string;
  school_address?: string;
  school_city?: string;
  school_country?: string;
  school_phone?: string;
  school_email?: string;
  is_online_only?: boolean;
  primary_color?: string;
  secondary_color?: string;
  school_ownership?: string;
  school_purposes?: string[];
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentSchoolId, setCurrentSchoolId] = useState<string | null>(
    localStorage.getItem('current_school_id')
  );

  // Load user on mount
  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    if (!localStorage.getItem('auth_token')) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await api.get<MeResponse>('/auth/me');
      // API returns { user: ... } directly (no .data wrapper)
      const userData = (response as any).user;
      setUser(userData);

      // Set default school if not set
      if (!currentSchoolId && userData.schools?.length > 0) {
        setCurrentSchoolId(userData.schools[0].id);
        localStorage.setItem('current_school_id', userData.schools[0].id);
      }
    } catch (error) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('current_school_id');
    } finally {
      setIsLoading(false);
    }
  };

  const refreshUser = async () => {
    if (!localStorage.getItem('auth_token')) return;

    try {
      const response = await api.get<MeResponse>('/auth/me');
      const userData = (response as any).user;
      setUser(userData);
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  const login = async (email: string, password: string) => {
    const response = await api.post<LoginResponse>('/auth/login', { email, password });
    // API returns { token, user } directly (no .data wrapper)
    const { token, user: userData } = response as any;
    
    localStorage.setItem('auth_token', token);
    api.setToken(token);
    setUser(userData);

    // Set default school
    if (userData.schools?.length > 0) {
      setCurrentSchoolId(userData.schools[0].id);
      localStorage.setItem('current_school_id', userData.schools[0].id);
    }
  };

  const register = async (data: RegisterData) => {
    const response = await api.post<LoginResponse>('/auth/register', data);
    // API returns { token, user } directly (no .data wrapper)
    const { token, user: userData } = response as any;

    localStorage.setItem('auth_token', token);
    api.setToken(token);
    setUser(userData);

    // Set default school if one was created
    if (userData.schools?.length > 0) {
      setCurrentSchoolId(userData.schools[0].id);
      localStorage.setItem('current_school_id', userData.schools[0].id);
    }
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('current_school_id');
      api.setToken(null);
      setUser(null);
      setCurrentSchoolId(null);
    }
  };

  const setCurrentSchool = (schoolId: string) => {
    setCurrentSchoolId(schoolId);
    localStorage.setItem('current_school_id', schoolId);
  };

  const currentSchool = user?.schools?.find(s => s.id === currentSchoolId) || user?.schools?.[0] || null;

  // Apply school theme colors as CSS variables
  useEffect(() => {
    const root = document.documentElement;
    if (currentSchool) {
      root.style.setProperty('--color-primary', currentSchool.primary_color);
      root.style.setProperty('--color-secondary', currentSchool.secondary_color);
      // Generate lighter/darker variants
      root.style.setProperty('--color-primary-light', `${currentSchool.primary_color}20`);
      root.style.setProperty('--color-primary-dark', currentSchool.primary_color);
    } else {
      // Default theme colors
      root.style.setProperty('--color-primary', '#14b8a6');
      root.style.setProperty('--color-secondary', '#f97316');
      root.style.setProperty('--color-primary-light', '#14b8a620');
      root.style.setProperty('--color-primary-dark', '#14b8a6');
    }
  }, [currentSchool]);

  // Determine if user is super admin
  const isSuperAdmin = user?.is_super_admin ?? false;

  // Determine user's primary role (super_admin takes precedence)
  const userRole: 'super_admin' | 'admin' | 'teacher' | 'student' | null =
    !user ? null :
    isSuperAdmin ? 'super_admin' :
    currentSchool?.role as 'admin' | 'teacher' | 'student' || null;

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        isSuperAdmin,
        userRole,
        login,
        register,
        logout,
        refreshUser,
        currentSchool,
        setCurrentSchool,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
